/**
 * `sgOwners` — the product line's published owner reviews riding on DealHunt `products_v2`
 * docs, beside `sgCoverage` (same contract family: DealHunt ships facts, this app owns
 * presentation). Absent when the node has zero published owner notes.
 *
 * Vocabulary: readers see "owner reviews"; the field and identifiers keep "owner"/"note".
 *
 * Parsing is tolerant, exactly like `parseSgCoverage`: a drifted shape degrades to "no owners
 * line" with a visible signal, never a crash of the typeahead. Absent ≠ invalid — and invalid
 * must never fall through to the zero-owners ask (a broken payload is not "nobody owns it").
 */
import { OWNER_DEFAULT_QUESTIONS } from './types'
import { lowerFirst } from 'lib/owner-notes/band-copy'

export interface SgOwnersTopNote {
  handle: string
  text: string
  /** Raw stored enum (`lt_1m` / `1_6m` / `6_12m` / `1_2y` / `gt_2y`); null = unanswered. */
  ownershipLength: string | null
  buyAgain: boolean | null
}

export interface SgOwners {
  /** Published owner reviews on the node (≥ 1 — the field is absent at zero). */
  count: number
  /** null when the sub-shape drifted (the title then drops its buy-again clause). */
  buyAgain: { yes: number; total: number } | null
  topNote: SgOwnersTopNote | null
}

export interface SgOwnersParse {
  /** null = no renderable owners (absent, or the shape failed validation). */
  owners: SgOwners | null
  /** True when a present value failed validation, fully or per sub-key. */
  invalid: boolean
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

function isCount(v: unknown): v is number {
  return typeof v === 'number' && Number.isInteger(v) && v >= 0
}

function optString(v: unknown): string | null {
  return typeof v === 'string' && v.trim().length > 0 ? v : null
}

function parseBuyAgain(v: unknown): SgOwners['buyAgain'] {
  if (!isRecord(v) || !isCount(v.yes) || !isCount(v.total) || v.yes > v.total) {
    return null
  }
  return { yes: v.yes, total: v.total }
}

function parseTopNote(v: unknown): SgOwnersTopNote | null {
  if (!isRecord(v)) {
    return null
  }
  const handle = optString(v.handle)
  const text = optString(v.text)
  if (!handle || !text) {
    return null
  }
  return {
    handle,
    text,
    ownershipLength: optString(v.ownershipLength),
    buyAgain: typeof v.buyAgain === 'boolean' ? v.buyAgain : null,
  }
}

/** Absent input (`undefined`) is honest "no owner reviews" — not invalid. */
export function parseSgOwners(raw: unknown): SgOwnersParse {
  if (raw === undefined) {
    return { owners: null, invalid: false }
  }
  if (!isRecord(raw) || !isCount(raw.count) || raw.count < 1) {
    return { owners: null, invalid: true }
  }
  let invalid = false
  const buyAgain = parseBuyAgain(raw.buyAgain)
  if (!buyAgain) {
    invalid = true
  }
  let topNote: SgOwnersTopNote | null = null
  // `topNote: null` is a legal value (no quotable note); only a present, drifted one is invalid.
  if (raw.topNote !== null) {
    topNote = parseTopNote(raw.topNote)
    if (!topNote) {
      invalid = true
    }
  }
  return { owners: { count: raw.count, buyAgain, topNote }, invalid }
}

/** What the typeahead's owners line renders for the top hit. */
export type OwnersLine = { kind: 'owners'; owners: SgOwners } | { kind: 'ask' } | null

/**
 * The owners line's decision, pure. `reviewed` = the hit carries a staff review line (the ask
 * is only offered beside one). The ask needs BOTH signals to agree nobody owns it: `sgOwners`
 * absent AND the long-standing scalar `ownerCount` absent (DealHunt's both-absent rule) —
 * `ownerCount` without `sgOwners` (backfill not yet run, or a missed reindex) renders nothing rather
 * than "No owner reviews yet" on a product that has them. An invalid shape renders nothing.
 */
export function resolveOwnersLine(
  parse: SgOwnersParse,
  ownerCount: number | undefined,
  reviewed: boolean
): OwnersLine {
  if (parse.owners) {
    return { kind: 'owners', owners: parse.owners }
  }
  if (parse.invalid || !reviewed) {
    return null
  }
  // Contract addendum (DealHunt, 2026-10-11): the ask only when BOTH fields are absent — any
  // `ownerCount` value at all (even a drifted 0) means the doc knows about owners.
  return ownerCount === undefined ? { kind: 'ask' } : null
}

/** "2 owners · 1 would buy again"; "1 owner" (no clause when nobody answered buy-again). */
export function ownersTitle(owners: SgOwners): string {
  const head = `${owners.count} owner${owners.count === 1 ? '' : 's'}`
  const ba = owners.buyAgain
  return ba && ba.total > 0 ? `${head} · ${ba.yes} would buy again` : head
}

export const OWNERS_QUOTE_MAX = 140

/** Cut at a word boundary at or before `max` chars, with an ellipsis; untouched when short. */
export function truncateQuote(text: string, max = OWNERS_QUOTE_MAX): string {
  const clean = text.trim().replace(/\s+/g, ' ')
  if (clean.length <= max) {
    return clean
  }
  const cut = clean.slice(0, max)
  const space = cut.lastIndexOf(' ')
  // No space in the window (one long token): hard-cut rather than return nothing.
  const head = space > 0 ? cut.slice(0, space) : cut
  return `${head.replace(/[\s,;:.!?—–-]+$/, '')}…`
}

/**
 * "6–12 months" for a stored ownership enum — the labels of DealHunt's default question set
 * (`OWNER_DEFAULT_QUESTIONS`, the same lookup `ownedLine` falls back to). Ids are stable across
 * question-set versions; an id the default set does not know returns null (clause dropped).
 */
export function ownershipLengthLabel(id: string | null): string | null {
  if (!id) {
    return null
  }
  return OWNER_DEFAULT_QUESTIONS.ownership_length.find(o => o.id === id)?.label ?? null
}

/** "— brisk_aurora_44, owner for 6–12 months" (no ownership clause when unknown). */
export function ownersAttribution(note: SgOwnersTopNote): string {
  const label = ownershipLengthLabel(note.ownershipLength)
  return label ? `— ${note.handle}, owner for ${lowerFirst(label)}` : `— ${note.handle}`
}
